import { useState } from 'react';
import { CalendarClock, MessageCircle, Phone } from 'lucide-react';
import { formatPhone } from '../../lib/format';
import { describeFollowup, followupPresets, telLink, toLocalInput, whatsappLink, type Tag } from '../../lib/leads';
import { TextField, cx } from '../ui';

export function StatusPill({ name, color }: { name: string; color: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: `${color}1f`, color }}>
      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="truncate">{name}</span>
    </span>
  );
}

export function TagChips({ tags, max = 3 }: { tags: Tag[]; max?: number }) {
  if (!tags.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {tags.slice(0, max).map((t) => (
        <span key={t.id} className="rounded px-1.5 py-0.5 text-[11px] font-medium" style={{ backgroundColor: `${t.color}1f`, color: t.color }}>{t.name}</span>
      ))}
      {tags.length > max && <span className="text-[11px] text-slate-500">+{tags.length - max}</span>}
    </span>
  );
}

export function FollowupBadge({ at }: { at: string | null }) {
  const f = describeFollowup(at);
  if (f.tone === 'none') return <span className="text-sm text-slate-400">—</span>;
  return (
    <span className={cx('inline-flex items-center gap-1 text-sm', f.tone === 'overdue' && 'font-medium text-red-600', f.tone === 'today' && 'font-medium text-amber-700 dark:text-amber-400', f.tone === 'later' && 'text-slate-600 dark:text-slate-400')}>
      <CalendarClock className="size-3.5 shrink-0" />
      {f.text}
    </span>
  );
}

/** One-tap call and WhatsApp. On phones "Call" opens the dialer. */
export function PhoneActions({ phone, size = 'md' }: { phone: string | null; size?: 'sm' | 'md' }) {
  if (!phone) return null;
  const cls = cx(
    'inline-flex items-center justify-center rounded-lg ring-1 ring-slate-200 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800',
    size === 'sm' ? 'size-8' : 'size-9',
  );
  return (
    <span className="inline-flex gap-1.5" onClick={(e) => e.stopPropagation()}>
      <a href={telLink(phone)} className={cls} title={`Call ${formatPhone(phone)}`} aria-label={`Call ${formatPhone(phone)}`}>
        <Phone className="size-4 text-brand-600" />
      </a>
      <a href={whatsappLink(phone)} target="_blank" rel="noopener noreferrer" className={cls} title="WhatsApp" aria-label="Open WhatsApp chat">
        <MessageCircle className="size-4 text-emerald-600" />
      </a>
    </span>
  );
}

/** Quick choices first ("Tomorrow morning"), with an exact date/time for everything else. */
export function FollowupPicker({ value, onChange, note, onNoteChange, error }: {
  value: string; onChange: (iso: string) => void; note?: string; onNoteChange?: (v: string) => void; error?: string;
}) {
  const presets = followupPresets();
  const [custom, setCustom] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Quick choices">
        {presets.map((p) => {
          const active = value && Math.abs(new Date(value).getTime() - p.date.getTime()) < 60_000;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => { setCustom(false); onChange(p.date.toISOString()); }}
              className={cx('rounded-full px-3 py-1.5 text-sm ring-1 transition', active ? 'bg-brand-600 text-white ring-brand-600' : 'ring-slate-300 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800')}
            >
              {p.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setCustom(true)}
          className={cx('rounded-full px-3 py-1.5 text-sm ring-1', custom ? 'bg-brand-600 text-white ring-brand-600' : 'ring-slate-300 hover:bg-slate-50 dark:ring-slate-700')}
        >
          Pick date & time
        </button>
      </div>
      {(custom || (value && !presets.some((p) => Math.abs(new Date(value).getTime() - p.date.getTime()) < 60_000))) && (
        <TextField
          label="Date and time"
          type="datetime-local"
          value={value ? toLocalInput(value) : ''}
          min={toLocalInput(new Date())}
          onChange={(e) => e.target.value && onChange(new Date(e.target.value).toISOString())}
          error={error}
        />
      )}
      {!custom && error && <p className="text-sm text-red-600">{error}</p>}
      {value && <p className="text-sm text-slate-600 dark:text-slate-400">Reminder set for <strong>{describeFollowup(value).text.replace(' (late)', '')}</strong></p>}
      {onNoteChange && (
        <TextField label="What to do (optional)" placeholder="e.g. Send price list, confirm date" value={note ?? ''} onChange={(e) => onNoteChange(e.target.value)} />
      )}
    </div>
  );
}
